import { useEffect, useState } from "react";

import {
  ArrowLeft,
  UserCog,
  Bell,
  ClipboardCheck,
  FileCheck2,
  Save,
  Lock,
  SlidersHorizontal,
} from "lucide-react";

import { useNavigate } from "react-router-dom";
import useGoBack from "../utils/useGoBack";
import { API_URL } from "../config";
import "./AdminSettings.css";

function AdminSettings() {
  const navigate = useNavigate();
  const goBack = useGoBack("/admin-dashboard");

  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  const [settings, setSettings] = useState({
    system_name: "",
    municipality: "",
    admin_office: "",
    livestock_types: "",
    default_listing_status: "Pending Approval",
    transaction_workflow: "Strict Workflow",
    account_deactivation: true,
    document_verification: true,
    admin_notifications: true,
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const token = localStorage.getItem("token");

        if (!token) {
          navigate("/login");
          return;
        }

        const res = await fetch(`${API_URL}/api/admin/settings`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await res.json();

        if (!res.ok) {
          setMessage(data.message || "Failed to load settings");
          setLoading(false);
          return;
        }

        setSettings({
          system_name: data.settings.system_name,
          municipality: data.settings.municipality,
          admin_office: data.settings.admin_office,
          livestock_types: data.settings.livestock_types,
          default_listing_status: data.settings.default_listing_status,
          transaction_workflow: data.settings.transaction_workflow,
          account_deactivation: Boolean(data.settings.account_deactivation),
          document_verification: Boolean(data.settings.document_verification),
          admin_notifications: Boolean(data.settings.admin_notifications),
        });

        setLoading(false);
      } catch (error) {
        setMessage("Cannot connect to backend server");
        setLoading(false);
      }
    };

    fetchSettings();
  }, [navigate]);

  const handleChange = (e) => {
    setSettings({
      ...settings,
      [e.target.name]: e.target.value,
    });
  };

  const handleToggle = (name) => {
    setSettings({
      ...settings,
      [name]: !settings[name],
    });
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(`${API_URL}/api/admin/settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(settings),
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.message || "Failed to save settings");
        return;
      }

      alert("Settings saved successfully");
    } catch (error) {
      alert("Cannot connect to backend server");
    }
  };

  if (loading) {
    return <h2 style={{ padding: "30px" }}>Loading settings...</h2>;
  }

  if (message) {
    return <h2 style={{ padding: "30px", color: "red" }}>{message}</h2>;
  }

  return (
    <div className="admin-settings-page">
      <div className="settings-header">
        <button type="button" className="back-btn" onClick={goBack} aria-label="Go back">
          <ArrowLeft size={20} />
        </button>

        <div>
          <h1>System Settings</h1>
          <p>
            Configure admin controls, security preferences, approval rules, and
            notification settings.
          </p>
        </div>
      </div>

      <section className="settings-grid">
        <SettingsCard
          accent="blue"
          icon={<UserCog />}
          title="Account Deactivation"
          text="Let admins deactivate farmer and buyer accounts."
          status={settings.account_deactivation ? "Allowed" : "Off"}
          tone={settings.account_deactivation ? "success" : "neutral"}
        />

        <SettingsCard
          accent="amber"
          icon={<ClipboardCheck />}
          title="Listing Approval"
          text="Require MAO review before listings appear publicly."
          status={settings.default_listing_status}
          tone="info"
        />

        <SettingsCard
          accent="green"
          icon={<FileCheck2 />}
          title="Document Verification"
          text="Require the barangay health certificate on livestock listings."
          status={settings.document_verification ? "Required" : "Optional"}
          tone={settings.document_verification ? "success" : "neutral"}
        />

        <SettingsCard
          accent="purple"
          icon={<Bell />}
          title="Admin Notifications"
          text="Receive alerts for new listings, documents, and transactions."
          status={settings.admin_notifications ? "Enabled" : "Disabled"}
          tone={settings.admin_notifications ? "success" : "neutral"}
        />
      </section>

      <div className="settings-panel">
        <div className="settings-panel-title">
          <div>
            <h3>General System Configuration</h3>
            <p>Basic marketplace rules and admin preferences.</p>
          </div>

          <span className="card-icon-badge">
            <SlidersHorizontal size={20} />
          </span>
        </div>

        <div className="settings-form">
          <div className="settings-group">
            <label>System Name</label>
            <input
              name="system_name"
              type="text"
              value={settings.system_name}
              onChange={handleChange}
            />
          </div>

          <div className="settings-group">
            <label>Municipality</label>
            <input
              name="municipality"
              type="text"
              value={settings.municipality}
              onChange={handleChange}
            />
          </div>

          <div className="settings-group">
            <label>Admin Office</label>
            <input
              name="admin_office"
              type="text"
              value={settings.admin_office}
              onChange={handleChange}
            />
          </div>

          <div className="settings-group">
            <label>Product Types (reference)</label>
            <input
              name="livestock_types"
              type="text"
              value={settings.livestock_types}
              onChange={handleChange}
            />
            <small className="settings-group-hint">
              For your records only — farmers choose from the built-in livestock and
              farm product list, or type their own under "Other".
            </small>
          </div>

          <div className="settings-group">
            <label>Default Listing Status</label>
            <select
              name="default_listing_status"
              value={settings.default_listing_status}
              onChange={handleChange}
            >
              <option>Pending Approval</option>
              <option>Auto Approved</option>
              <option>Draft</option>
            </select>
          </div>

          <div className="settings-group">
            <label>Default Transaction Workflow</label>
            <select
              name="transaction_workflow"
              value={settings.transaction_workflow}
              onChange={handleChange}
            >
              <option>Strict Workflow</option>
              <option>Flexible Workflow</option>
            </select>
          </div>
        </div>
      </div>

      <div className="settings-panel">
        <div className="settings-panel-title">
          <div>
            <h3>Security and Access Settings</h3>
            <p>Sign-in and role permissions are always enforced. Manage account and document rules here.</p>
          </div>

          <span className="card-icon-badge">
            <Lock size={20} />
          </span>
        </div>

        <div className="settings-list">
          <SettingToggle
            accent="purple"
            icon={<UserCog />}
            title="Allow account deactivation"
            text="Admins can deactivate farmer and buyer accounts. Reactivating is always allowed."
            active={settings.account_deactivation}
            onChange={() => handleToggle("account_deactivation")}
          />

          <SettingToggle
            accent="green"
            icon={<FileCheck2 />}
            title="Require document verification"
            text="Livestock listings must include the barangay health certificate. When off, documents are optional."
            active={settings.document_verification}
            onChange={() => handleToggle("document_verification")}
          />

          <SettingToggle
            accent="amber"
            icon={<Bell />}
            title="Enable admin notification alerts"
            text="Notify admin when new actions require review."
            active={settings.admin_notifications}
            onChange={() => handleToggle("admin_notifications")}
          />
        </div>
      </div>

      <div className="settings-actions">
        <button className="save-settings-btn" type="button" onClick={handleSave}>
          <Save size={18} />
          Save Settings
        </button>
      </div>
    </div>
  );
}

function SettingsCard({ accent = "green", icon, title, text, status, tone = "info" }) {
  return (
    <div className={`settings-card accent-${accent}`}>
      <div className="settings-icon">{icon}</div>

      <div>
        <h3>{title}</h3>
        <p>{text}</p>
      </div>

      <span className={`settings-status ${tone}`}>{status}</span>
    </div>
  );
}

function SettingToggle({ accent = "green", icon, title, text, active, onChange }) {
  return (
    <div className={`setting-toggle accent-${accent} ${active ? "is-active" : ""}`}>
      <div className="setting-toggle-icon">{icon}</div>

      <div>
        <strong>{title}</strong>
        <p>{text}</p>
      </div>

      <label className="switch">
        <input type="checkbox" checked={active} onChange={onChange} />
        <span className="slider"></span>
      </label>
    </div>
  );
}

export default AdminSettings;